import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarMember from "../../components/Sidebar/SidebarMember";
import { db, auth } from "../../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  doc,
  updateDoc,
  increment,
  getDoc,
} from "firebase/firestore";

export default function MyGroups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bids, setBids] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [selectedGroupForEmergency, setSelectedGroupForEmergency] = useState(null);
  const [emergencyAmount, setEmergencyAmount] = useState("");
  const [emergencyMonth, setEmergencyMonth] = useState("");
  const [emergencyReason, setEmergencyReason] = useState("");
  const user = auth.currentUser;

  // Fetch all groups
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "groups"), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGroups(data);
    });

    return () => unsub();
  }, []);

  // Fetch join requests for this member
  useEffect(() => {
    if (!user?.uid) {
      console.log("❌ User UID not available");
      return;
    }

    console.log("🔍 Fetching requests for user:", user.uid);
    const q = query(
      collection(db, "joinRequests"),
      where("memberId", "==", user.uid)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log("✅ Requests fetched:", data);
      setRequests(data);
    });

    return () => unsub();
  }, [user]);

  // Fetch payments for this member
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "payments"), where("memberId", "==", user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPayments(data);
    });
    return () => unsub();
  }, [user]);

  // Fetch bids for this member
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "bids"), where("memberId", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => setBids(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [user]);

  // Fetch notifications for this member
  useEffect(() => {
    if (!user?.uid) {
      console.log("❌ Notifications: User UID not available");
      return;
    }
    console.log("🔍 Fetching notifications for user:", user.uid);
    const q = query(collection(db, "notifications"), where("to", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      console.log("✅ Notifications fetched:", data);
      setNotifications(data);
    });
    return () => unsub();
  }, [user]);

  // Redirect to payment menu
  const handlePayment = (group) => {
    navigate(`/member/payments?groupId=${group.id}`);
  };

  // Make a payment (simulated): creates a payment record, updates joinRequest, notifies organizer
  const makePayment = async (group) => {
    if (!group.contribution || group.contribution <= 0) {
      alert("Invalid contribution amount");
      return;
    }

    const options = {
      key: "rzp_test_1DP5mmOlF5G1ag", // Replace with your Razorpay Key ID
      amount: group.contribution * 100, // Amount in paise (₹ to paise)
      currency: "INR",
      name: group.groupName,
      description: `Payment for ${group.groupName}`,
      prefill: {
        name: user.displayName || "Member",
        email: user.email || "",
      },
      handler: async function (response) {
        try {
          // Payment successful - record in database
          await addDoc(collection(db, "payments"), {
            groupId: group.id,
            groupName: group.groupName,
            memberId: user.uid,
            memberName: user.displayName || "Member",
            amount: group.contribution,
            status: "completed",
            razorpayPaymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id || null,
            razorpaySignature: response.razorpay_signature || null,
            createdAt: serverTimestamp(),
          });

          // update joinRequest (mark lastPaidAt)
          const req = requests.find((r) => r.groupId === group.id && r.memberId === user.uid);
          if (req) {
            const reqRef = doc(db, "joinRequests", req.id);
            await updateDoc(reqRef, { lastPaidAt: serverTimestamp(), paid: true });
          }

          // update group totals
          const groupRef = doc(db, "groups", group.id);
          await updateDoc(groupRef, {
            totalCollected: increment(group.contribution || 0),
            membersPaid: increment(1),
          });

          // notify organizer
          if (group.organizerId) {
            await addDoc(collection(db, "notifications"), {
              to: group.organizerId,
              type: "payment",
              message: `${user.displayName || 'Member'} paid ₹${group.contribution} for ${group.groupName}`,
              groupId: group.id,
              memberId: user.uid,
              createdAt: serverTimestamp(),
              read: false,
            });
          }

          alert(`Payment Successful!\n\nAmount: ₹${group.contribution}\nGroup: ${group.groupName}\nTransaction ID: ${response.razorpay_payment_id}\n\nOrganizer has been notified.`);
        } catch (err) {
          console.error("Error recording payment:", err);
          alert("Payment completed but failed to record. Contact support with Transaction ID: " + response.razorpay_payment_id);
        }
      },
      modal: {
        ondismiss: function () {
          alert("Payment cancelled. Please try again.");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // Request to join a group
  const requestJoin = async (group) => {
    try {
      // prevent duplicate
      const existing = requests.find((r) => r.groupId === group.id && r.memberId === user.uid);
      if (existing) {
        alert("You already have a request for this group.");
        return;
      }

      // Get user profile to retrieve phone number
      let memberPhone = "";
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          memberPhone = userDocSnap.data().phone || userDocSnap.data().phoneNumber || "";
        }
      } catch (err) {
        console.warn("Could not fetch user phone number:", err);
      }

      // If phone number is missing, prompt user to add it
      if (!memberPhone || memberPhone === "" || memberPhone === "0000000000") {
        const phoneInput = prompt("📱 Please enter your 10-digit phone number to continue:\n\nThis will be shared with the organizer.", "");
        
        if (!phoneInput) {
          alert("Phone number is required to send a join request.");
          return;
        }

        // Validate phone number (10 digits)
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(phoneInput)) {
          alert("❌ Invalid phone number. Please enter exactly 10 digits.");
          return;
        }

        memberPhone = phoneInput;

        // Update user profile with phone number
        try {
          const userDocRef = doc(db, "users", user.uid);
          await updateDoc(userDocRef, {
            phone: memberPhone,
            phoneNumber: memberPhone, // Store in both fields for compatibility
          });
          console.log("✅ Phone number saved to user profile");
        } catch (err) {
          console.error("Error saving phone number:", err);
          // Continue anyway - we have the phone number for this request
        }
      }

      await addDoc(collection(db, "joinRequests"), {
        groupId: group.id,
        groupName: group.groupName,
        organizerId: group.organizerId || null,
        memberId: user.uid,
        memberName: user.displayName || "Member",
        memberEmail: user.email || "",
        memberPhone: memberPhone,
        status: "processing",
        createdAt: serverTimestamp(),
      });

      // notify organizer
      if (group.organizerId) {
        await addDoc(collection(db, "notifications"), {
          to: group.organizerId,
          type: "join_request",
          message: `${user.displayName || 'Member'} requested to join ${group.groupName}`,
          groupId: group.id,
          memberId: user.uid,
          createdAt: serverTimestamp(),
          read: false,
        });
      }

      alert("Request sent — status: processing.");
    } catch (err) {
      console.error(err);
      alert("Failed to send request. Check console for errors.");
    }
  };

  // Open emergency request modal
  const openEmergencyModal = (group) => {
    setSelectedGroupForEmergency(group);
    setEmergencyAmount(group.contribution || "");
    setEmergencyMonth("");
    setEmergencyReason("");
    setShowEmergencyModal(true);
  };

  // Close emergency modal
  const closeEmergencyModal = () => {
    setShowEmergencyModal(false);
    setSelectedGroupForEmergency(null);
    setEmergencyAmount("");
    setEmergencyMonth("");
    setEmergencyReason("");
  };

  // Submit emergency request
  const submitEmergencyRequest = async () => {
    if (!selectedGroupForEmergency) return;

    // Validation
    if (!emergencyAmount || Number(emergencyAmount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    if (!emergencyMonth) {
      alert("Please select the month for which you need emergency funds");
      return;
    }

    if (!emergencyReason || emergencyReason.trim().length < 10) {
      alert("Please provide a detailed reason (at least 10 characters)");
      return;
    }

    try {
      // Create bid document
      await addDoc(collection(db, "bids"), {
        groupId: selectedGroupForEmergency.id,
        groupName: selectedGroupForEmergency.groupName,
        memberId: user.uid,
        memberName: user.displayName || "Member",
        amount: Number(emergencyAmount),
        month: emergencyMonth,
        reason: emergencyReason.trim(),
        status: "pending",
        createdAt: serverTimestamp(),
        organizerId: selectedGroupForEmergency.organizerId || null,
      });

      // Notify organizer
      if (selectedGroupForEmergency.organizerId) {
        await addDoc(collection(db, "notifications"), {
          to: selectedGroupForEmergency.organizerId,
          type: "bid_request",
          message: `${user.displayName || 'Member'} requested emergency funds of ₹${emergencyAmount} for ${emergencyMonth} in ${selectedGroupForEmergency.groupName}. Reason: ${emergencyReason.substring(0, 50)}${emergencyReason.length > 50 ? '...' : ''}`,
          groupId: selectedGroupForEmergency.id,
          memberId: user.uid,
          createdAt: serverTimestamp(),
          read: false,
        });
      }

      alert(`✅ Emergency request sent successfully!\n\nAmount: ₹${emergencyAmount}\nMonth: ${emergencyMonth}\n\nThe organizer will review your request.`);
      closeEmergencyModal();
    } catch (error) {
      console.error("Error submitting emergency request:", error);
      alert("Failed to submit emergency request. Please try again.");
    }
  };

  // Get current and next months for selection
  const getMonthOptions = () => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const monthName = date.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
      months.push(monthName);
    }
    return months;
  };

  // Leave group: member can leave an accepted group
  const leaveGroup = async (groupId) => {
    if (!window.confirm("Are you sure you want to leave this group?")) {
      return;
    }

    try {
      const req = requests.find((r) => r.groupId === groupId && r.memberId === user.uid);
      if (req) {
        const reqRef = doc(db, "joinRequests", req.id);
        await updateDoc(reqRef, { status: "left" });
        alert("You have left the group.");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to leave group. Check console for errors.");
    }
  };

  // Show all requests with their status
  const getStatusColor = (status) => {
    if (status === "accepted") return { bg: "#d4edda", color: "#155724", label: "Accepted" };
    if (status === "pending") return { bg: "#fff3cd", color: "#856404", label: "Pending" };
    if (status === "rejected") return { bg: "#f8d7da", color: "#721c24", label: "Rejected" };
    return { bg: "#e2e3e5", color: "#383d41", label: "Unknown" };
  };

  return (
    <div style={{ display: 'flex', width: '100%', height: '100vh' }}>
      <SidebarMember />
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.pageTitle}>My Groups</h1>
          <p style={styles.subtitle}>Manage your group memberships and payments</p>
        </div>

        {/* Payment Alerts Section */}
        <div style={styles.alertsSection}>
          <h2 style={styles.sectionTitle}>Payment Alerts</h2>
          {(() => {
            const alerts = [];
            requests.filter(r => r.status === 'accepted' && r.status !== 'removed' && r.status !== 'left').forEach(req => {
              const lastPayment = payments
                .filter(p => p.groupId === req.groupId)
                .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0];
              
              const lastPaidDate = lastPayment?.createdAt?.toDate ? new Date(lastPayment.createdAt.seconds * 1000) : null;
              const daysSincePayment = lastPaidDate ? Math.floor((Date.now() - lastPaidDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
              const group = groups.find(g => g.id === req.groupId);
              
              if (daysSincePayment && daysSincePayment >= 28) {
                alerts.push({
                  type: 'overdue',
                  req, group, daysSincePayment, lastPaidDate
                });
              } else if (daysSincePayment && daysSincePayment >= 26) {
                alerts.push({
                  type: 'warning',
                  req, group, daysSincePayment, lastPaidDate
                });
              }
            });
            
            if (alerts.length === 0) {
              return <div style={styles.noAlerts}>No pending payments</div>;
            }
            
            return alerts.map((alert, idx) => {
              const { type, req, group, daysSincePayment, lastPaidDate } = alert;
              const isOverdue = type === 'overdue';
              
              return (
                <div key={idx} style={{ 
                  ...styles.alertCard, 
                  borderLeft: `4px solid ${isOverdue ? '#dc2626' : '#f59e0b'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, color: isOverdue ? '#dc2626' : '#f59e0b' }}>
                        {isOverdue ? `Payment Overdue (${daysSincePayment} days)` : 'Payment Due Soon'}: {req.groupName}
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#6b7280' }}>Last paid: {lastPaidDate?.toLocaleDateString()}</p>
                    </div>
                    <button onClick={() => makePayment(group)} style={styles.alertButton}>Pay Now</button>
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* My Joined Groups Section */}
        <h2 style={styles.sectionTitle}>Active Groups</h2>
        {requests.filter(r => r.status === 'accepted' && r.status !== 'removed' && r.status !== 'left').length === 0 ? (
          <p style={{ color: '#6b7280' }}>No groups joined yet</p>
        ) : (
          <div style={styles.groupsContainer}>
            {requests.filter(r => r.status === 'accepted' && r.status !== 'removed' && r.status !== 'left').map(req => {
              const group = groups.find(g => g.id === req.groupId);
              const groupPayments = payments.filter(p => p.groupId === req.groupId);
              const lastPayment = groupPayments.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0];
              const lastPaidDate = lastPayment?.createdAt?.toDate ? new Date(lastPayment.createdAt.seconds * 1000).toLocaleDateString() : 'Never';
              
              return (
                <div key={req.id} style={styles.groupCard}>
                  <div style={styles.cardHeader}>
                    <div>
                      <h3 style={styles.groupTitle}>{req.groupName}</h3>
                      <p style={styles.cardSubtitle}>Group contribution plan</p>
                    </div>
                    <div style={styles.statusBadge}>Active</div>
                  </div>

                  <div style={styles.cardStats}>
                    <div style={styles.statBox}>
                      <p style={styles.statLabel}>Monthly Amount</p>
                      <p style={styles.statValue}>₹{group?.contribution || '—'}</p>
                    </div>
                    <div style={styles.statBox}>
                      <p style={styles.statLabel}>Total Money</p>
                      <p style={styles.statValue}>₹{group?.totalMoney || (group?.maxMembers * group?.contribution) || '—'}</p>
                    </div>
                    <div style={styles.statBox}>
                      <p style={styles.statLabel}>Duration</p>
                      <p style={styles.statValue}>{group?.months || '—'} months</p>
                    </div>
                    <div style={styles.statBox}>
                      <p style={styles.statLabel}>Commission</p>
                      <p style={styles.statValue}>₹{group?.commission || '—'}/month</p>
                    </div>
                    <div style={styles.statBox}>
                      <p style={styles.statLabel}>Group Size</p>
                      <p style={styles.statValue}>{group?.currentMembers || '—'}/{group?.maxMembers || '—'}</p>
                    </div>
                    <div style={styles.statBox}>
                      <p style={styles.statLabel}>Last Payment</p>
                      <p style={styles.statValue}>{lastPaidDate}</p>
                    </div>
                  </div>

                  {group?.description && (
                    <p style={styles.groupDescription}>{group.description}</p>
                  )}

                  <div style={styles.paymentHistory}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 600 }}>Payment History</h4>
                    {groupPayments.length === 0 ? (
                      <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>No payments yet</p>
                    ) : (
                      groupPayments
                        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                        .slice(0, 3)
                        .map(p => (
                          <div key={p.id} style={styles.paymentRow}>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                              {p.createdAt?.toDate ? new Date(p.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>+₹{p.amount}</span>
                          </div>
                        ))
                    )}
                  </div>

                  <div style={styles.actionButtons}>
                    <button onClick={() => handlePayment(group)} style={styles.primaryButton}>Make Payment</button>
                    <button onClick={() => openEmergencyModal(group)} style={styles.secondaryButton}>Emergency Request</button>
                    <button onClick={() => leaveGroup(group.id)} style={styles.dangerButton}>Leave Group</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Emergency Requests Section */}
        {bids.length > 0 && (
          <>
            <h2 style={styles.sectionTitle}>Emergency Requests</h2>
            <div style={styles.requestsContainer}>
              {bids
                .sort((a, b) => {
                  const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds || 0) * 1000;
                  const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds || 0) * 1000;
                  return bTime - aTime;
                })
                .map(bid => {
                  const group = groups.find(g => g.id === bid.groupId);
                  const statusColor = bid.status === 'approved' ? '#059669' : bid.status === 'rejected' ? '#dc2626' : '#f59e0b';
                  const statusBg = bid.status === 'approved' ? '#ecfdf5' : bid.status === 'rejected' ? '#fef2f2' : '#fffbeb';
                  const statusBorder = bid.status === 'approved' ? '#a7f3d0' : bid.status === 'rejected' ? '#fecaca' : '#fde68a';
                  
                  return (
                    <div 
                      key={bid.id} 
                      style={{
                        ...styles.requestCard,
                        borderLeft: `4px solid ${statusColor}`,
                        backgroundColor: statusBg,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <h4 style={{ margin: 0, fontSize: 14, fontWeight: '600' }}>{bid.groupName || 'Unknown Group'}</h4>
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            backgroundColor: statusColor,
                            color: '#fff',
                            borderRadius: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                          }}>
                            {bid.status || 'pending'}
                          </span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <p style={{ margin: '4px 0', fontSize: '13px', color: '#111827' }}>
                            <strong>Amount:</strong> ₹{bid.amount}
                          </p>
                          {bid.month && (
                            <p style={{ margin: '4px 0', fontSize: '13px', color: '#111827' }}>
                              <strong>For Month:</strong> {bid.month}
                            </p>
                          )}
                          <p style={{ margin: '4px 0', fontSize: '13px', color: '#111827' }}>
                            <strong>Reason:</strong> {bid.reason}
                          </p>
                          {bid.createdAt && (
                            <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>
                              Requested: {
                                bid.createdAt.toDate 
                                  ? new Date(bid.createdAt.toDate()).toLocaleDateString()
                                  : bid.createdAt.seconds
                                  ? new Date(bid.createdAt.seconds * 1000).toLocaleDateString()
                                  : 'N/A'
                              }
                            </p>
                          )}
                          {bid.respondedAt && (
                            <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>
                              Responded: {
                                bid.respondedAt.toDate 
                                  ? new Date(bid.respondedAt.toDate()).toLocaleDateString()
                                  : bid.respondedAt.seconds
                                  ? new Date(bid.respondedAt.seconds * 1000).toLocaleDateString()
                                  : 'N/A'
                              }
                            </p>
                          )}
                          {bid.organizerReply && (
                            <div style={{
                              marginTop: '8px',
                              padding: '10px',
                              backgroundColor: '#fff',
                              borderRadius: '6px',
                              border: `1px solid ${statusBorder}`,
                            }}>
                              <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                                Organizer's Reply:
                              </p>
                              <p style={{ margin: 0, fontSize: '13px', color: '#374151', fontStyle: 'italic' }}>
                                "{bid.organizerReply}"
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}

        {/* Pending Requests Section */}
        {requests.filter(r => r.status === 'processing' || r.status === 'pending').length > 0 && (
          <>
            <h2 style={styles.sectionTitle}>Pending Requests</h2>
            <div style={styles.requestsContainer}>
              {requests.filter(r => r.status === 'processing' || r.status === 'pending').map(req => (
                <div key={req.id} style={styles.requestCard}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>{req.groupName}</h4>
                  <p style={{ margin: '4px 0', fontSize: 12, color: '#6b7280' }}>Status: <strong>{req.status === 'processing' ? 'Processing' : 'Pending'}</strong></p>
                  <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>Contribution: ₹{req.contribution || '—'}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Emergency Request Modal */}
        {showEmergencyModal && selectedGroupForEmergency && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
            }}
            onClick={closeEmergencyModal}
          >
            <div 
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '550px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 30px rgba(2,6,23,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>
                  Emergency Fund Request
                </h2>
                <button
                  onClick={closeEmergencyModal}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.color = '#111827';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1e40af', fontWeight: '600' }}>
                  Group: {selectedGroupForEmergency.groupName}
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#3b82f6' }}>
                  Monthly Contribution: ₹{selectedGroupForEmergency.contribution || 0}
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                  Requested Amount (₹) <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  value={emergencyAmount}
                  onChange={(e) => setEmergencyAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="1"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.outline = 'none';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                  Default: Monthly contribution amount (₹{selectedGroupForEmergency.contribution || 0})
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                  For Which Month? <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={emergencyMonth}
                  onChange={(e) => setEmergencyMonth(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.outline = 'none';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">Select month</option>
                  {getMonthOptions().map((month, index) => (
                    <option key={index} value={month}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                  Reason for Emergency Request <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  value={emergencyReason}
                  onChange={(e) => setEmergencyReason(e.target.value)}
                  placeholder="Please provide a detailed reason for your emergency fund request (minimum 10 characters)..."
                  rows="5"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.outline = 'none';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                  {emergencyReason.length} / Minimum 10 characters
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
                <button
                  onClick={closeEmergencyModal}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e5e7eb';
                    e.target.style.color = '#111827';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.color = '#374151';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitEmergencyRequest}
                  disabled={!emergencyAmount || !emergencyMonth || emergencyReason.trim().length < 10}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (!emergencyAmount || !emergencyMonth || emergencyReason.trim().length < 10) ? 'not-allowed' : 'pointer',
                    border: 'none',
                    backgroundColor: (!emergencyAmount || !emergencyMonth || emergencyReason.trim().length < 10) ? '#9ca3af' : '#2563eb',
                    color: '#fff',
                    transition: 'all 0.2s ease',
                    boxShadow: (!emergencyAmount || !emergencyMonth || emergencyReason.trim().length < 10) ? 'none' : '0 2px 8px rgba(37,99,235,0.2)',
                  }}
                  onMouseEnter={(e) => {
                    if (emergencyAmount && emergencyMonth && emergencyReason.trim().length >= 10) {
                      e.target.style.backgroundColor = '#1d4ed8';
                      e.target.style.boxShadow = '0 4px 12px rgba(37,99,235,0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (emergencyAmount && emergencyMonth && emergencyReason.trim().length >= 10) {
                      e.target.style.backgroundColor = '#2563eb';
                      e.target.style.boxShadow = '0 2px 8px rgba(37,99,235,0.2)';
                    }
                  }}
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= INLINE STYLES ================= */

const styles = {
  page: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    padding: "30px",
    fontFamily: "'Segoe UI', sans-serif",
    flex: 1,
    width: "100%",
    overflowY: "auto",
    marginLeft: "220px",
  },

  header: {
    marginBottom: '40px',
  },

  pageTitle: {
    fontSize: "28px",
    fontWeight: 700,
    margin: '0 0 8px 0',
    color: '#111827',
  },

  subtitle: {
    margin: 0,
    color: '#6b7280',
    fontSize: '14px',
  },

  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    margin: '28px 0 16px 0',
    color: '#111827',
  },

  alertsSection: {
    marginBottom: '30px',
  },

  noAlerts: {
    padding: '12px',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    color: '#0369a1',
    fontSize: '14px',
  },

  alertCard: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '14px',
    marginBottom: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },

  alertButton: {
    padding: '6px 14px',
    backgroundColor: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },

  groupsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
    gap: "20px",
  },

  groupCard: {
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    transition: "all 0.2s ease",
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f3f4f6',
  },

  groupTitle: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: 700,
    color: '#1f2937',
  },

  cardSubtitle: {
    margin: 0,
    fontSize: '12px',
    color: '#9ca3af',
  },

  statusBadge: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },

  cardStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
    marginBottom: '16px',
  },

  statBox: {
    backgroundColor: '#f9fafb',
    padding: '10px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #f3f4f6',
  },

  statLabel: {
    margin: 0,
    fontSize: '11px',
    color: '#6b7280',
    fontWeight: 500,
    textTransform: 'uppercase',
  },

  statValue: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    fontWeight: 700,
    color: '#1f2937',
  },

  groupDescription: {
    margin: '12px 0',
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.5',
  },

  paymentHistory: {
    backgroundColor: '#f9fafb',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '14px',
    border: '1px solid #f3f4f6',
  },

  paymentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: '1px solid #e5e7eb',
  },

  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },

  primaryButton: {
    flex: 1,
    minWidth: '100px',
    padding: '10px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '13px',
    transition: 'background 0.2s',
  },

  secondaryButton: {
    flex: 1,
    minWidth: '100px',
    padding: '10px',
    backgroundColor: '#f3f4f6',
    color: '#1f2937',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '13px',
    transition: 'background 0.2s',
  },

  dangerButton: {
    flex: 1,
    minWidth: '100px',
    padding: '10px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '13px',
    transition: 'background 0.2s',
  },

  requestsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '12px',
  },

  requestCard: {
    backgroundColor: '#fff',
    border: '1px solid #fef3c7',
    borderLeft: '4px solid #f59e0b',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },

  bidsSection: {
    marginTop: "12px",
    padding: "10px",
    backgroundColor: "#fff3cd",
    borderRadius: "8px",
  },

  bidItem: {
    fontSize: 13,
    color: "#856404",
    marginBottom: "6px",
  },

  actions: {
    display: "flex",
    gap: "10px",
    marginTop: "16px",
  },

  payBtn: {
    flex: 1,
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: 14,
  },

  emergencyBtn: {
    flex: 1,
    backgroundColor: "#0d6efd",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: 14,
  },

  disabledBtn: {
    flex: 1,
    backgroundColor: "#fff3cd",
    color: "#856404",
    border: "1px solid #ffc107",
    padding: "12px",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "not-allowed",
    fontSize: 14,
  },

  rejectedBtn: {
    flex: 1,
    backgroundColor: "#f8d7da",
    color: "#721c24",
    border: "1px solid #f5c6cb",
    padding: "12px",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "not-allowed",
    fontSize: 14,
  },

    joinBtn: {
      width: "100%",
      backgroundColor: "#0d6efd",
      color: "#fff",
      border: "none",
      padding: "12px",
      borderRadius: "8px",
      fontWeight: "600",
      cursor: "pointer",
      fontSize: 14,
    },

  requestsList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "16px",
  },

  requestItem: {
    padding: "16px",
    borderRadius: "10px",
    border: "1px solid #ddd",
    transition: "all 0.3s ease",
  },
};
